import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  BookOpen,
  Briefcase,
  ChevronDown,
  Clock,
  Compass,
  GraduationCap,
  Lightbulb,
  MapPin,
  Route,
  Send,
  Sparkles,
  Users,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';

function capitalize(text) {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function parseCompanies(raw) {
  if (!raw) return [];
  const cleaned = raw
    .replace(/^(companies|organizations|hospitals|firms|agencies|colleges|institutes|studios|pharma companies|working with|working at)\s+(like|such as|including)\s*/i, '')
    .replace(/\s+(and|&)\s+/gi, ', ');
  return cleaned.split(',').map(s => s.trim()).filter(s => s.length > 0 && s.length < 60);
}

/** Split text into sentences, handling common abbreviations */
function splitSentences(text) {
  if (!text) return [];
  return text
    .replace(/([.!?])\s+/g, '$1\n')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
}

function ContentSection({ icon: Icon, iconBg, iconColor, title, children, testId, delay }) {
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-7 animate-fade-in-up"
      style={{ animationDelay: `${delay}s`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
        <h2 className="text-base sm:text-lg font-bold text-[#1F2937] font-['Outfit']">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function CareerDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [career, setCareer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const mentorSectionRef = useRef(null);

  const fetchCareer = useCallback(async () => {
    try {
      const res = await api.get(`/careers/${slug}`);
      setCareer(res.data);
    } catch (err) {
      if (err.response?.status === 404) toast.error('Career not found');
      else toast.error('Failed to load career');
      navigate('/careers');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  const fetchChildren = useCallback(async () => {
    if (!user || user.role !== 'parent') return;
    try {
      const res = await api.get('/children');
      const valid = res.data.filter((c) => c.status === 'approved' || c.status === 'active');
      setChildren(valid);
      if (valid.length === 1) setSelectedChildId(valid[0].id);
    } catch {}
  }, [user]);

  useEffect(() => { fetchCareer(); fetchChildren(); }, [fetchCareer, fetchChildren]);

  useEffect(() => {
    if (!selectedChildId || !career) { setIsSaved(false); return; }
    const child = children.find((c) => c.id === selectedChildId);
    if (child) setIsSaved((child.saved_career_ids || []).includes(career.slug));
  }, [selectedChildId, career, children]);

  const handleSaveToggle = async () => {
    if (!selectedChildId) { toast.error('Please select a child first'); return; }
    setSaveLoading(true);
    try {
      if (isSaved) {
        await api.delete(`/children/${selectedChildId}/saved-careers/${career.slug}`);
        toast.success('Career removed from saved list');
      } else {
        await api.post(`/children/${selectedChildId}/saved-careers`, { career_slug: career.slug });
        toast.success('Career saved successfully');
      }
      const res = await api.get('/children');
      setChildren(res.data.filter((c) => c.status === 'approved' || c.status === 'active'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update saved careers');
    }
    setSaveLoading(false);
  };

  const handleRequestExperience = () => {
    if (user?.role === 'parent') {
      navigate(`/careers/${career.slug}/book`);
    } else {
      navigate(`/login?returnTo=/careers/${career.slug}`);
    }
  };

  const scrollToMentor = () => {
    mentorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 animate-pulse">
          <div className="h-7 w-28 bg-gray-200 rounded mb-5" />
          <div className="rounded-2xl bg-gray-100 aspect-[21/8] mb-5" />
          <div className="grid lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 space-y-4">
              <div className="h-40 bg-gray-100 rounded-2xl" />
              <div className="h-28 bg-gray-100 rounded-2xl" />
            </div>
            <div className="lg:col-span-2 h-64 bg-gray-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!career) return null;

  const companies = parseCompanies(career.real_world_example);

  return (
    <div className="py-5 sm:py-6" data-testid="career-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back */}
        <Button
          variant="ghost"
          onClick={() => navigate('/careers')}
          className="text-sm text-[#6B7280] hover:text-[#1F2937] mb-4 -ml-2 rounded-lg h-8 px-2"
          data-testid="back-to-explore-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Explore
        </Button>

        {/* ═══ HERO ═══ */}
        <div className="relative rounded-2xl overflow-hidden mb-6 animate-fade-in-up" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="career-hero">
          <div className="relative bg-gray-900 h-[220px] sm:h-[280px] lg:h-[320px]">
            {(career.image_hero_url || career.image_url) ? (
              <img
                src={career.image_hero_url || career.image_url}
                alt={career.name}
                className="w-full h-full object-cover object-[center_20%]"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                <Briefcase className="h-16 w-16 text-white/15" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] leading-tight"
                data-testid="career-title"
              >
                {career.name}
              </h1>
              {career.short_description && (
                <p className="text-sm sm:text-base text-white/80 mt-2 leading-relaxed max-w-2xl">
                  {career.short_description}
                </p>
              )}
              {career.mentor_session_focus && (
                <button
                  onClick={scrollToMentor}
                  className="mt-3 inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs sm:text-sm font-medium transition-colors"
                  data-testid="hero-experience-cta"
                >
                  Experience This Career <ChevronDown className="h-3.5 w-3.5 animate-bounce" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ═══ MAIN GRID ═══ */}
        <div className="grid lg:grid-cols-5 gap-5 lg:gap-6">
          {/* ─── LEFT: Career Understanding (second on mobile) ─── */}
          <div className="lg:col-span-3 order-last lg:order-first space-y-5">
            {/* 1. Full Career Overview */}
            {career.explanation && (
              <ContentSection icon={Lightbulb} iconBg="bg-[#FFF0ED]" iconColor="text-[#F97D6A]" title="Full Career Overview" testId="career-explanation" delay={0.06}>
                <div className="space-y-3">
                  {splitSentences(career.explanation).map((s, i) => (
                    <p key={i} className="text-[#4B5563] leading-[1.8] text-sm sm:text-[15px]">{s}</p>
                  ))}
                </div>
              </ContentSection>
            )}

            {/* 2. A Day in This Career */}
            {career.day_in_life && (
              <ContentSection icon={Clock} iconBg="bg-blue-50" iconColor="text-blue-500" title="A Day in This Career" testId="career-day-in-life" delay={0.1}>
                <div className="space-y-3">
                  {splitSentences(career.day_in_life).map((s, i) => (
                    <p key={i} className="text-[#4B5563] leading-[1.8] text-sm sm:text-[15px]">{s}</p>
                  ))}
                </div>
              </ContentSection>
            )}

            {/* 3. Your Path After School */}
            {career.path_after_school && (
              <ContentSection icon={Route} iconBg="bg-indigo-50" iconColor="text-indigo-500" title="Your Path After School" testId="career-path-after-school" delay={0.14}>
                <ul className="space-y-2">
                  {splitSentences(capitalize(career.path_after_school)).map((s, i) => (
                    <li key={i} className="flex gap-2.5 text-[#4B5563] leading-[1.8] text-sm sm:text-[15px]">
                      <span className="mt-[9px] h-1.5 w-1.5 rounded-full bg-indigo-400 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </ContentSection>
            )}
          </div>

          {/* ─── RIGHT: Decision Panel (first on mobile) ─── */}
          <div className="lg:col-span-2 order-first lg:order-last space-y-5">
            <div
              className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 animate-fade-in-up"
              style={{ animationDelay: '0.08s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
              data-testid="quick-snapshot"
            >
              <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mb-5">Quick Snapshot</h3>

              {/* Stream */}
              {career.stream && (
                <div className="mb-5">
                  <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 block">Stream</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${
                    career.stream === 'Science' ? 'bg-sky-50 text-sky-700 border-sky-200'
                    : career.stream === 'Commerce' ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : career.stream === 'Any Stream' ? 'bg-violet-50 text-violet-700 border-violet-200'
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                  }`} data-testid="career-stream-badge">
                    <BookOpen className="h-3 w-3" />{career.stream}
                  </span>
                </div>
              )}

              {/* Admission Requirements */}
              {career.admission_subjects?.length > 0 && (
                <div className="mb-5">
                  <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 block">Admission Requirements</span>
                  <div className="flex flex-wrap gap-1.5">
                    {career.admission_subjects.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-red-100">
                        <GraduationCap className="h-3 w-3" />{s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Subjects to Select */}
              {(career.recommended_subjects?.length > 0 || career.subjects?.length > 0) && (
                <div className="mb-5">
                  <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 block">Subjects to Select</span>
                  <div className="flex flex-wrap gap-1.5">
                    {(career.recommended_subjects || career.subjects || []).map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-100">
                        <GraduationCap className="h-3 w-3" />{s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Helpful Subjects */}
              {career.helpful_subjects?.length > 0 && (
                <div className="mb-5">
                  <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 block">Helpful Subjects</span>
                  <div className="flex flex-wrap gap-1.5">
                    {career.helpful_subjects.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-100">
                        <GraduationCap className="h-3 w-3" />{s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Preparation Style */}
                {career.preparation_style && (
                  <div>
                    <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider block mb-1">Preparation Style</span>
                    <span className="text-sm font-medium text-[#1F2937] leading-relaxed">{career.preparation_style}</span>
                  </div>
                )}

                {/* Where You'll Work */}
                {career.work_environment && (
                  <div>
                    <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider block mb-1">Where You'll Work</span>
                    <span className="text-sm font-medium text-[#1F2937] first-letter:uppercase leading-relaxed">{career.work_environment}</span>
                  </div>
                )}

                {/* Who This Is Best For */}
                {career.best_fit_for && (
                  <div>
                    <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider block mb-1">Who This Is Best For</span>
                    <span className="text-sm font-medium text-[#1F2937] first-letter:uppercase leading-relaxed">{career.best_fit_for}</span>
                  </div>
                )}
              </div>

              {/* Top Companies */}
              {companies.length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 block">Top Companies & Organizations</span>
                  <div className="flex flex-wrap gap-1.5">
                    {companies.map((name, i) => (
                      <span key={i} className="inline-flex items-center bg-violet-50 text-violet-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-violet-100">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Save for Child (parent-only) */}
            {user?.role === 'parent' && (
              <div
                className="bg-white rounded-2xl border border-gray-100 p-5 animate-fade-in-up"
                style={{ animationDelay: '0.12s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                data-testid="save-career-card"
              >
                <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Save for Your Child</h3>
                {children.length === 0 ? (
                  <div>
                    <p className="text-xs text-[#9CA3AF] mb-3">Add a child first to save careers.</p>
                    <Button onClick={() => navigate('/parent')} variant="outline" className="w-full rounded-xl h-9 text-sm" data-testid="add-child-btn">
                      Go to Dashboard
                    </Button>
                  </div>
                ) : children.length > 1 ? (
                  <>
                    <Select value={selectedChildId} onValueChange={setSelectedChildId}>
                      <SelectTrigger className="h-9 rounded-xl border-gray-200 text-sm mb-3" data-testid="child-selector">
                        <SelectValue placeholder="Select a child" />
                      </SelectTrigger>
                      <SelectContent>
                        {children.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.full_name} (Grade {c.grade})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleSaveToggle}
                      disabled={saveLoading || !selectedChildId}
                      className={`w-full rounded-xl h-9 text-sm font-semibold ${isSaved ? 'bg-[#F5F6F8] text-[#4B5563] hover:bg-gray-200 border border-gray-200' : 'bg-[#F97D6A] text-white hover:bg-[#F06651]'}`}
                      data-testid="save-career-btn"
                    >
                      {saveLoading ? 'Saving...' : isSaved ? <><BookmarkCheck className="h-4 w-4 mr-1" />Saved</> : <><Bookmark className="h-4 w-4 mr-1" />Save Career</>}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-[#6B7280] mb-3">
                      Saving for <span className="font-semibold text-[#1F2937]">{children[0]?.full_name}</span>
                    </p>
                    <Button
                      onClick={handleSaveToggle}
                      disabled={saveLoading}
                      className={`w-full rounded-xl h-9 text-sm font-semibold ${isSaved ? 'bg-[#F5F6F8] text-[#4B5563] hover:bg-gray-200 border border-gray-200' : 'bg-[#F97D6A] text-white hover:bg-[#F06651]'}`}
                      data-testid="save-career-btn"
                    >
                      {saveLoading ? 'Saving...' : isSaved ? <><BookmarkCheck className="h-4 w-4 mr-1" />Saved</> : <><Bookmark className="h-4 w-4 mr-1" />Save Career</>}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ═══ FULL-WIDTH MENTOR EXPERIENCE SECTION ═══ */}
        {career.mentor_session_focus && (
          <div
            ref={mentorSectionRef}
            className="mt-8 rounded-2xl overflow-hidden animate-fade-in-up"
            style={{ animationDelay: '0.22s', background: 'linear-gradient(135deg, #FFF8F6 0%, #FFF0ED 50%, #FDE8E4 100%)' }}
            data-testid="mentor-experience-section"
          >
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-lg bg-[#F97D6A]/15 flex items-center justify-center shrink-0">
                  <Sparkles className="h-4.5 w-4.5 text-[#F97D6A]" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-[#1F2937] font-['Outfit']">
                  Experience This Career with a Professional
                </h2>
              </div>
              <p className="text-[#4B5563] leading-[1.8] text-sm sm:text-[15px] mb-6 max-w-3xl whitespace-pre-line">
                {capitalize(career.mentor_session_focus)}
              </p>
              <Button
                onClick={handleRequestExperience}
                className="rounded-full h-10 sm:h-11 px-6 sm:px-8 text-sm sm:text-base font-semibold bg-[#F97D6A] text-white hover:bg-[#F06651] shadow-sm"
                data-testid="request-experience-btn"
              >
                <Send className="h-4 w-4 mr-2" /> Request a Career Experience
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
